import {Subtitle, SubtitleStyle} from "@/models/Subtitle";
import {isEqual} from "@/utils/objectUtils";

export interface SubtitleMetadata {
    videoWidth?: number;
    videoHeight?: number;
}

export interface SubtitleBounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface SubtitleWithBounds {
    id: string;
    startTimeMs: number;
    endTimeMs: number;
    text: string;
    bounds: SubtitleBounds[];
}

export interface SerializedSubtitleData {
    style?: SubtitleStyle;
    metadata?: SubtitleMetadata;
    subtitles: Array<{
        id: string;
        startTimeMs: number;
        endTimeMs: number;
        text: string;
        style?: SubtitleStyle;
    }>;
}

export class SubtitleData {
    style?: SubtitleStyle;
    metadata: SubtitleMetadata;
    subtitles: Subtitle[];

    constructor({ subtitles, style, metadata }: { subtitles: Subtitle[]; style?: SubtitleStyle; metadata?: SubtitleMetadata }) {
        this.subtitles = subtitles;
        this.style = style;
        this.metadata = metadata || {};
    }

    serialize(): SerializedSubtitleData {
        const activeSubtitles = this.subtitles
            .filter(sub => !sub.deleted)
            .sort((a, b) => a.startTimeMs - b.startTimeMs);

        const subtitlesWithStyles = activeSubtitles.filter(
            sub => Object.keys(sub.style).length > 0
        );

        let documentStyle: SubtitleStyle | undefined;

        if (subtitlesWithStyles.length === activeSubtitles.length) {
            const styleCounts = new Map<string, { style: SubtitleStyle; count: number }>();

            for (const subtitle of subtitlesWithStyles) {
                // Line bounds fit one cue's lines, so they never become the document default
                const {lineBounds, ...sharedStyle} = subtitle.style;
                const styleKey = JSON.stringify(sharedStyle);
                const existing = styleCounts.get(styleKey);

                if (existing) {
                    existing.count++;
                } else {
                    styleCounts.set(styleKey, { style: sharedStyle, count: 1 });
                }
            }

            let maxCount = 0;
            for (const {style, count} of styleCounts.values()) {
                if (count > maxCount) {
                    maxCount = count;
                    documentStyle = style;
                }
            }
        }

        const serializedSubtitles = activeSubtitles.map((subtitle, idx) => {
            const shouldOmitStyle = documentStyle &&
                Object.keys(subtitle.style).length > 0 &&
                isEqual(subtitle.style, documentStyle);

            return {
                id: String(idx + 1),
                startTimeMs: subtitle.startTimeMs,
                endTimeMs: subtitle.endTimeMs,
                text: subtitle.text,
                style: shouldOmitStyle ? undefined : (Object.keys(subtitle.style).length > 0 ? subtitle.style : undefined)
            };
        });

        return {
            style: documentStyle,
            metadata: this.metadata,
            subtitles: serializedSubtitles
        };
    }

    static deserialize(data: SerializedSubtitleData): SubtitleData {
        const subtitles = data.subtitles.map(item => {
            const style = item.style || data.style || {};

            return new Subtitle({
                id: item.id,
                startTimeMs: item.startTimeMs,
                endTimeMs: item.endTimeMs,
                text: item.text,
                style
            });
        });

        return new SubtitleData({ subtitles, style: data.style, metadata: data.metadata });
    }
}
